import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  CheckCircle,
  Paperclip,
  Sparkle,
  UploadSimple,
  X,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { API } from "@/lib/api";
import { shrinkImage } from "@/lib/shrinkImage";

const SMS_CONSENT =
  "Yes, text me about my quote, slab holds and install dates at the number above. Consent is not a condition of purchase. Message frequency varies; message and data rates may apply. Reply STOP to opt out, HELP for help. See our";

const EMPTY = {
  name: "",
  company: "",
  phone: "",
  email: "",
  address: "",
  project_type: "Kitchen",
  timeline: "",
  budget: "",
  working_with: "",
  colors: [],
  other_colors: "",
  message: "",
  files: [],
  source: "Website form",
};
const Field = ({ label, required, children, hint }) => (
  <label className="block space-y-1.5">
    <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-400">
      {label}
      {required && <span className="ml-1 text-[#c39e6d]">*</span>}
    </span>
    <span className="lead-field relative block">{children}</span>
    {hint && <span className="block text-[11px] text-zinc-500">{hint}</span>}
  </label>
);

export default function LeadForm({
  embedded = false,
  prefillColor = "",
  prefillMessage = "",
  source = "",
  onClose,
}) {
  const [params] = useSearchParams();
  const fromShowroom = prefillColor || params.get("color") || "";
  const edge = params.get("edge") || "";
  const edgeMessage = edge
    ? `I'd like the ${edge} edge${fromShowroom ? ` in ${fromShowroom}` : ""} on my countertops.`
    : "";
  const [opts, setOpts] = useState({
    colors: [],
    project_types: [],
    timelines: [],
    company: "",
  });
  const [f, setF] = useState({
    ...EMPTY,
    sms_opt_in: false,
    sms_opt_in_text: "",
    colors: fromShowroom ? [fromShowroom] : [],
    other_colors: fromShowroom,
    message: prefillMessage || edgeMessage || EMPTY.message,
    source: source || (edge ? "Edges page" : fromShowroom ? "Showroom page" : EMPTY.source),
  });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    fetch(`${API}/public/lead-form`)
      .then((r) => r.json())
      .then(setOpts)
      .catch(() => {});
  }, []);

  const set = (patch) => setF((cur) => ({ ...cur, ...patch }));

  const uploadFiles = async (fileList) => {
    const list = Array.from(fileList || []);
    if (!list.length) return;
    setUploading(true);
    for (const file of list) {
      const body = new FormData();
      body.append("file", await shrinkImage(file));
      try {
        const res = await fetch(`${API}/public/leads/upload`, {
          method: "POST",
          body,
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || "Upload failed");
        setF((cur) => ({ ...cur, files: [...cur.files, data] }));
        toast.success(`${file.name} attached`);
      } catch (err) {
        toast.error(`${file.name}: ${err.message}`);
      }
    }
    setUploading(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) return toast.error("Tell us your name");
    if (!f.phone.trim() && !f.email.trim())
      return toast.error("Add a phone number or an email");
    if (!f.address.trim()) return toast.error("Add the project address");
    if (!f.timeline) return toast.error("Pick a timeline");
    setBusy(true);
    try {
      const res = await fetch(`${API}/public/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(f),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Could not send that");
      setDone(data.code);
      requestAnimationFrame(() =>
        document
          .querySelector('[data-testid="lead-thanks"]')
          ?.scrollIntoView({ behavior: "smooth", block: "center" }),
      );
    } catch (err) {
      toast.error(err.message || "Could not send that");
    }
    setBusy(false);
  };

  if (done)
    return (
      <div
        className={
          embedded
            ? "px-2 py-6 text-center"
            : "grain flex min-h-screen items-center justify-center px-5"
        }
      >
        <div
          className="max-w-md rounded-2xl border border-zinc-800 bg-zinc-900 p-8 text-center"
          data-testid="lead-thanks"
        >
          <CheckCircle
            size={38}
            weight="fill"
            className="mx-auto text-emerald-400"
          />
          <h1 className="mt-4 text-2xl font-bold tracking-tight">Thank you</h1>
          <p className="mt-2 text-sm leading-relaxed text-zinc-400">
            Your enquiry is with our team. Reference{" "}
            <span className="font-mono-data text-[#d8b26a]">{done}</span> — we
            will be in touch shortly.
          </p>
          <Button
            data-testid="lead-another"
            onClick={() => {
              if (embedded && onClose) return onClose();
              setF({ ...EMPTY });
              setDone(null);
            }}
            className={`mt-6 h-11 w-full text-xs font-bold uppercase tracking-widest ${
              embedded
                ? "s-btn"
                : "bg-[#c39e6d] text-black hover:bg-[#d8b26a]"
            }`}
          >
            {embedded ? "Close" : "Send another enquiry"}
          </Button>
        </div>
      </div>
    );

  return (
    <div className={embedded ? "" : "grain min-h-screen px-5 py-10 sm:py-16"}>
      <form
        onSubmit={submit}
        className={embedded ? "lead-embedded" : "mx-auto max-w-3xl"}
        data-testid="lead-form"
      >
        <div
          className={`flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.28em] ${
            embedded ? "s-micro text-[color:var(--s-gold)]" : "text-[#d8b26a]"
          }`}
        >
          <Sparkle size={13} weight="fill" />
          {opts.company || "Brioni Granite"}
        </div>
        <h1
          className={
            embedded
              ? "s-display mt-6 text-3xl sm:text-4xl"
              : "mt-3 text-4xl font-bold tracking-tight sm:text-5xl"
          }
        >
          Tell us about your countertops
        </h1>
        <p className="mt-4 max-w-xl text-base leading-relaxed text-zinc-400">
          Send us the basics and the colors you have your eye on. We will come
          back with a quote and slab availability.
        </p>
        {fromShowroom && (
          <p
            className={`mt-4 inline-flex px-4 py-2 text-xs ${
              embedded
                ? "border border-[color:var(--s-line)] text-[color:var(--s-gold)]"
                : "rounded-full border border-[#c39e6d]/30 bg-[#c39e6d]/10 text-[#e2c88f]"
            }`}
            data-testid="lead-from-showroom"
          >
            Asking about <strong className="mx-1">{fromShowroom}</strong> from
            the showroom
          </p>
        )}

        <div className="mt-10 space-y-8">
          <section className="grid gap-4 sm:grid-cols-2">
            <Field label="Your name" required>
              <Input
                data-testid="lead-name"
                value={f.name}
                onChange={(e) => set({ name: e.target.value })}
                placeholder="Jane Cooper"
                className="h-12 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <Field label="Company">
              <Input
                data-testid="lead-company"
                value={f.company}
                onChange={(e) => set({ company: e.target.value })}
                placeholder="Optional"
                className="h-12 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <Field label="Phone" hint="Phone or email — at least one">
              <Input
                data-testid="lead-phone"
                value={f.phone}
                onChange={(e) => set({ phone: e.target.value })}
                placeholder="(651) 555 0134"
                className="h-12 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <Field label="Email">
              <Input
                data-testid="lead-email"
                type="email"
                value={f.email}
                onChange={(e) => set({ email: e.target.value })}
                placeholder="jane@example.com"
                className="h-12 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <Field label="Project address" required>
              <Input
                data-testid="lead-address"
                value={f.address}
                onChange={(e) => set({ address: e.target.value })}
                placeholder="3000 Minnesota Ave, Unit 2"
                className="h-12 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <Field label="Project type">
              <Select
                value={f.project_type}
                onValueChange={(v) => set({ project_type: v })}
              >
                <SelectTrigger
                  data-testid="lead-project-type"
                  className="h-12 border-zinc-800 bg-zinc-950"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(opts.project_types || []).map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Timeline" required>
              <Select
                value={f.timeline}
                onValueChange={(v) => set({ timeline: v })}
              >
                <SelectTrigger
                  data-testid="lead-timeline"
                  className="h-12 border-zinc-800 bg-zinc-950"
                >
                  <SelectValue placeholder="When do you need it?" />
                </SelectTrigger>
                <SelectContent>
                  {(opts.timelines || []).map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Budget">
              <Input
                data-testid="lead-budget"
                value={f.budget}
                onChange={(e) => set({ budget: e.target.value })}
                placeholder="Optional"
                className="h-12 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <Field
              label="Working with"
              hint="Your builder, designer or whoever you have been dealing with"
            >
              <Input
                data-testid="lead-working-with"
                value={f.working_with}
                onChange={(e) => set({ working_with: e.target.value })}
                placeholder="Sam at Lopez Builders"
                className="h-12 border-zinc-800 bg-zinc-950"
              />
            </Field>
          </section>

          <section>
            <Field
              label="Colors or ideas"
              hint="Tell us the looks you like — we will match them to slabs in stock"
            >
              <Input
                data-testid="lead-other-colors"
                value={f.other_colors}
                onChange={(e) => set({ other_colors: e.target.value })}
                placeholder="Calacatta look-alikes, dark soapstone…"
                className="h-12 border-zinc-800 bg-zinc-950"
              />
            </Field>
          </section>

          <section>
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">
              Photos or plans
            </div>
            <label
              className="mt-3 flex cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed border-zinc-700 bg-zinc-950/60 px-6 py-8 text-center transition-colors duration-150 hover:border-[#c39e6d]/60 hover:bg-zinc-900"
              data-testid="lead-upload-zone"
            >
              <UploadSimple
                size={22}
                weight="bold"
                className="text-[#d8b26a]"
              />
              <span className="mt-2 text-sm font-semibold text-zinc-200">
                {uploading ? "Uploading…" : "Add photos, sketches or a plan"}
              </span>
              <span className="mt-1 text-[11px] text-zinc-500">
                JPG, PNG, HEIC or PDF · up to 12 MB each
              </span>
              <input
                data-testid="lead-upload-input"
                type="file"
                multiple
                accept=".jpg,.jpeg,.png,.webp,.heic,.gif,.pdf"
                className="hidden"
                onChange={(e) => uploadFiles(e.target.files)}
              />
            </label>
            {f.files.length > 0 && (
              <div className="mt-3 space-y-2" data-testid="lead-files">
                {f.files.map((file) => (
                  <div
                    key={file.id}
                    data-testid={`lead-file-${file.id}`}
                    className="flex items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2.5"
                  >
                    <Paperclip
                      size={14}
                      weight="bold"
                      className="text-zinc-500"
                    />
                    <span className="min-w-0 flex-1 truncate text-xs text-zinc-300">
                      {file.name}
                    </span>
                    <span className="font-mono-data shrink-0 text-[10px] text-zinc-500">
                      {Math.max(1, Math.round((file.size || 0) / 1024))} KB
                    </span>
                    <button
                      type="button"
                      data-testid={`lead-file-remove-${file.id}`}
                      onClick={() =>
                        set({ files: f.files.filter((x) => x.id !== file.id) })
                      }
                      className="text-zinc-600 transition-colors duration-150 hover:text-red-400"
                    >
                      <X size={14} weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>

          <Field label="Anything else we should know">
            <Textarea
              data-testid="lead-message"
              value={f.message}
              onChange={(e) => set({ message: e.target.value })}
              placeholder="Rough sizes, sink you already own, when the cabinets go in…"
              className="min-h-[120px] border-zinc-800 bg-zinc-950"
            />
          </Field>

          <label
            data-testid="lead-sms-consent"
            className={`flex cursor-pointer gap-3 border p-4 ${
              embedded
                ? "border-[color:var(--s-line)] bg-[color:var(--s-surface)]"
                : "rounded-lg border-zinc-800 bg-zinc-950"
            }`}
          >
            <input
              type="checkbox"
              data-testid="lead-sms-optin"
              checked={f.sms_opt_in}
              onChange={(e) =>
                set({
                  sms_opt_in: e.target.checked,
                  sms_opt_in_text: e.target.checked ? SMS_CONSENT : "",
                })
              }
              className="mt-0.5 h-4 w-4 shrink-0 accent-[#c39e6d]"
            />
            <span className="text-xs leading-relaxed text-zinc-400">
              {SMS_CONSENT}{" "}
              <a
                href="/terms"
                target="_blank"
                rel="noreferrer"
                data-testid="lead-terms-link"
                className="underline"
              >
                Terms
              </a>{" "}
              &amp;{" "}
              <a
                href="/privacy"
                target="_blank"
                rel="noreferrer"
                data-testid="lead-privacy-link"
                className="underline"
              >
                Privacy policy
              </a>
              .
            </span>
          </label>

          <Button
            data-testid="lead-submit"
            type="submit"
            disabled={busy}
            className={`h-14 w-full transition-colors duration-150 sm:w-auto ${
              embedded
                ? "s-btn !h-[3.75rem] sm:!px-9"
                : "bg-[#c39e6d] text-xs font-bold uppercase tracking-[0.2em] text-black hover:bg-[#d8b26a] sm:px-10"
            }`}
          >
            {busy ? "Sending…" : "Send my enquiry"}
          </Button>
        </div>
      </form>
    </div>
  );
}
